import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { useUser } from '../../auth/session';
import { AuthCard } from '../../components/Layout';

/** "Allow Claude to use Couch?" — the OAuth consent for an app's connector (doc 37). */
export function ConsentPage() {
  const user = useUser();
  const { search } = useLocation();
  const params = Object.fromEntries(new URLSearchParams(search));
  const request = useQuery({ queryKey: ['oauth-request', search], queryFn: () => api.oauthRequest(search), retry: false });
  const decide = useMutation({
    mutationFn: (allow: boolean) => api.oauthDecide(params, allow),
    onSuccess: ({ redirectTo }) => { window.location.assign(redirectTo); },
  });
  if (request.isPending) return <AuthCard title="Connect an app"><p role="status">Checking the request…</p></AuthCard>;
  if (request.isError) {
    return <AuthCard title="This connection cannot be made"><p role="alert" className="form-error">{errorMessage(request.error)}</p></AuthCard>;
  }
  const { clientName, redirectHost } = request.data;
  return (
    <AuthCard title={`Connect ${clientName} to Couch?`}>
      <p>Signed in as <strong>{user.displayName}</strong>. {clientName} (returning to <code>{redirectHost}</code>) will be able to:</p>
      <ul>
        <li>see your machines, models and custom nodes</li>
        <li>build, check and run workflows, and look at the pictures</li>
        <li>download safetensors models and ask you before custom nodes</li>
      </ul>
      <p className="muted">It cannot see your account, passwords or tokens, or anyone else's things. Disconnect it any time in Account → Connected apps.</p>
      {decide.isError && <p role="alert" className="form-error">{errorMessage(decide.error)}</p>}
      <p>
        <button type="button" disabled={decide.isPending || decide.isSuccess} onClick={() => { decide.mutate(true); }}>Allow</button>{' '}
        <button type="button" className="button-quiet" disabled={decide.isPending || decide.isSuccess} onClick={() => { decide.mutate(false); }}>Deny</button>
      </p>
    </AuthCard>
  );
}
